import * as THREE from 'three'
import { ProtoBase } from '../ProtoBase'
import { SITES } from '../kanjinData'

type Phase = 'load' | 'ground' | 'ascend' | 'orbit' | 'finale'

/**
 * ⑩ そらとぶズームアウト v4 — 泰山荘（実写・地理院タイル）から畳ごと上昇。
 * 雲海を突き抜けた後は白木に転換せず、**実写の日本列島が「青い星」のように**
 * 星空に浮かぶ地球ビューで完結する。フィナーレは実写地図の上で、
 * 12寄進地から三鷹へ光の筋が収束する。実寸系: 1unit = 4m。
 */
export class Proto10 extends ProtoBase {
  readonly title = '10. そらとぶズームアウト'
  readonly desc = '泰山荘の実写から急上昇→雲を突き抜け→実写の日本列島が地球のように浮かぶ→日本中から光が三鷹へ収束（出典: 地理院タイル）'

  private phase: Phase = 'load'
  private phaseT = 0
  private alt = 0
  private clouds: THREE.Mesh[] = []
  private stars!: THREE.Points
  private starMat!: THREE.PointsMaterial
  private flash!: THREE.Sprite
  private pin!: THREE.Sprite
  private fog = new THREE.Fog(0x9fc8e8, 260, 1500)
  private burstDone = false
  private starTarget = 0

  // 地球ビュー・フィナーレ（実写地図の上の光の収束）
  private earthMask!: THREE.Mesh
  private fx = new THREE.Group() // aerial.root の子（実写地図ローカル座標・1u=4m）
  private fxBuilt = false
  private centerGlow!: THREE.Sprite
  private streaks: { line: THREE.Line; head: THREE.Sprite; from: THREE.Vector3; t: number; delay: number; done: boolean }[] = []
  private allArrivedT = 0

  private static readonly TOP = 640
  private static readonly CLOUD_LO = 195
  private static readonly CLOUD_HI = 235
  /** 軌道ズーム終端: z6グリッド(約4,069km≈101.7万u)が視界に収まる縮尺 */
  private static readonly SCALE_END = 640 / 1017222
  private halo!: THREE.Sprite
  private earthGlow!: THREE.Sprite
  private cloudGone = false

  private skyGround = new THREE.Color(0x9fc8e8)
  private skyHigh = new THREE.Color(0x27466e)
  private skySpace = new THREE.Color(0x070d1e)
  private skyWhite = new THREE.Color(0xf2f4f7)

  protected start(): void {
    this.ctx.setWorld('void')
    this.ctx.scene.background = this.skyGround.clone()

    // ピン（泰山荘・地上用）
    this.pin = Proto10.makePin(0xd63f2f)
    this.pin.position.set(0, 5, 0)
    this.pin.scale.setScalar(4)
    this.group.add(this.pin)

    // 地球ビュー用の縁マスク: 実写地図の縁を丸く宇宙色に溶かし「星の上の国」に見せる
    this.earthMask = Proto10.makeEarthMask(this.skySpace)
    this.earthMask.rotation.x = -Math.PI / 2
    this.earthMask.position.y = 2
    this.earthMask.scale.setScalar(1500)
    this.earthMask.visible = false
    this.group.add(this.earthMask)

    // 雲海3層（厚みのある帯）
    const cloudTex = Proto10.makeCloudTexture()
    for (const [y, s, o] of [
      [Proto10.CLOUD_LO + 8, 1.0, 0.95],
      [Proto10.CLOUD_LO + 40, 1.5, 0.8],
      [Proto10.CLOUD_HI - 6, 2.2, 0.6],
    ] as const) {
      const m = new THREE.Mesh(
        new THREE.PlaneGeometry(7000 * s, 7000 * s),
        new THREE.MeshBasicMaterial({ map: cloudTex, transparent: true, opacity: o, depthWrite: false }),
      )
      m.rotation.x = -Math.PI / 2
      m.position.y = y
      this.clouds.push(m)
      this.group.add(m)
    }

    // 星空（宇宙の入口）
    const starGeo = new THREE.BufferGeometry()
    const pos: number[] = []
    for (let i = 0; i < 900; i++) {
      const v = new THREE.Vector3().randomDirection().multiplyScalar(3600)
      if (v.y < -300) v.y *= -1
      pos.push(v.x, v.y, v.z)
    }
    starGeo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
    this.starMat = new THREE.PointsMaterial({
      color: 0xdfe8ff, size: 5, sizeAttenuation: true, transparent: true, opacity: 0,
      blending: THREE.AdditiveBlending, depthWrite: false,
    })
    this.stars = new THREE.Points(starGeo, this.starMat)
    this.stars.frustumCulled = false
    this.group.add(this.stars)

    // 雲抜けの白フラッシュ
    this.flash = new THREE.Sprite(new THREE.SpriteMaterial({
      map: Proto10.makeGlowTexture(), color: 0xffffff, transparent: true, opacity: 0, depthWrite: false, depthTest: false,
    }))
    this.flash.scale.setScalar(60)
    this.group.add(this.flash)

    // 大気の光（地球ビュー用）: 日本の背後に青いハロー
    this.halo = Proto10.makeAtmosphereHalo()
    this.halo.position.set(0, -120, -600)
    this.halo.scale.setScalar(2600)
    this.halo.material.opacity = 0
    this.group.add(this.halo)
    this.earthGlow = Proto10.makeAtmosphereHalo()
    this.earthGlow.position.set(0, -260, 0)
    this.earthGlow.scale.setScalar(3600)
    this.earthGlow.material.opacity = 0
    this.group.add(this.earthGlow)

    // 畳デッキ＋武四郎はこの演出ではPCでも表示（CG同乗）
    this.ctx.deck.setVisible(true)
    if (this.ctx.narrator.loaded) this.ctx.narrator.root.visible = true

    if (this.ctx.aerial.isLoaded) {
      this.enterGround()
    } else {
      this.phase = 'load'
      this.ctx.setCaption('航空写真（地理院タイル）を読み込み中…')
      void this.ctx.aerial.load().then(() => {
        if (this.phase === 'load') this.enterGround()
      })
    }
  }

  private enterGround(): void {
    this.phase = 'ground'
    this.phaseT = 0
    this.alt = 0
    this.burstDone = false
    this.cloudGone = false
    this.starTarget = 0
    this.starMat.opacity = 0
    this.halo.material.opacity = 0
    this.earthGlow.material.opacity = 0
    this.earthMask.visible = false
    this.resetFx()
    for (const c of this.clouds) {
      c.visible = true
      ;(c.material as THREE.MeshBasicMaterial).opacity = 0.85
    }
    this.ctx.aerial.root.visible = true
    this.ctx.aerial.root.scale.setScalar(1)
    this.ctx.aerial.root.position.set(0, 0, 0)
    this.ctx.aerial.setAllOpacity(1)
    this.ctx.aerial.updateForAltitude(0)
    this.ctx.terrain.root.visible = false
    this.ctx.markers.root.visible = false
    this.pin.visible = true
    this.ctx.scene.background = this.skyGround.clone()
    // 地平線ぼかし: 距離フォグ（空の色に溶ける）
    this.fog.color.copy(this.skyGround)
    this.fog.near = 240
    this.fog.far = 1400
    this.ctx.scene.fog = this.fog
    this.setPoseForAltitude(0)
    this.ctx.setCaption('ここが泰山荘——森の中に、わしの一畳がある')
  }

  private setPoseForAltitude(alt: number): void {
    if (this.ctx.view.xrActive) {
      this.ctx.view.setPose(new THREE.Vector3(0, alt, 0), new THREE.Vector3(0, alt, -10))
    } else {
      this.ctx.view.setPose(
        new THREE.Vector3(0, alt + 14, 30),
        new THREE.Vector3(0, Math.max(0, alt - 40), -70),
      )
    }
  }

  /** 高度に応じた上昇速度（u/s）。★超スピード: 離陸→日本列島まで数秒で駆け上がる */
  private speedAt(alt: number): number {
    if (alt < 40) return 30 + (alt / 40) * 190 // 一気に加速
    if (alt < Proto10.CLOUD_HI + 12) return 280 // ★雲帯ごと最高速で突破
    if (alt < Proto10.TOP - 90) return 250 // 列島が開けるまで落とさない
    return 60 + Math.max(0, ((Proto10.TOP - alt) / 90)) * 80 // 地球ビュー直前で減速
  }

  update(dt: number): void {
    this.elapsed += dt
    this.phaseT += dt
    const bg = this.ctx.scene.background as THREE.Color

    // 星の明滅とフェード
    this.starMat.opacity += (this.starTarget - this.starMat.opacity) * Math.min(1, dt * 1.5)
    const fm = this.flash.material as THREE.SpriteMaterial
    if (fm.opacity > 0) fm.opacity = Math.max(0, fm.opacity - dt * 1.6)

    switch (this.phase) {
      case 'load':
        break

      case 'ground':
        this.pin.position.y = 5 + Math.sin(this.elapsed * 2) * 0.8
        if (this.phaseT > 4) {
          this.phase = 'ascend'
          this.phaseT = 0
          this.ctx.setCaption('参るぞ——摑まっておれ！')
          this.ctx.sfx.play('whoosh')
          this.ctx.sfx.play('chime')
        }
        break

      case 'ascend': {
        this.alt = Math.min(Proto10.TOP, this.alt + this.speedAt(this.alt) * dt)
        this.setPoseForAltitude(this.alt)
        this.ctx.aerial.updateForAltitude(this.alt)
        this.flash.position.set(0, this.alt + 5, -20)

        // フォグ: 高度が上がるほど遠くまで見える→宇宙域で消す
        this.fog.far = 1400 + this.alt * 14
        this.fog.near = 240 + this.alt * 4

        // 空色: 地上→高空→（雲帯で白）→宇宙
        const inCloud = this.alt > Proto10.CLOUD_LO - 30 && this.alt < Proto10.CLOUD_HI + 15
          ? Math.max(0, 1 - Math.abs(this.alt - (Proto10.CLOUD_LO + Proto10.CLOUD_HI) / 2) / 90)
          : 0
        const spaceU = Math.max(0, (this.alt - 440) / (Proto10.TOP - 440))
        const base = this.skyGround.clone()
          .lerp(this.skyHigh, Math.min(1, this.alt / 420))
          .lerp(this.skySpace, spaceU)
        bg.copy(base.lerp(this.skyWhite, inCloud))
        this.fog.color.copy(bg)
        if (spaceU > 0.15) {
          this.starTarget = Math.min(1, spaceU * 1.6)
          this.ctx.scene.fog = null // 宇宙域はフォグなしで列島をくっきり
        }

        // 雲突入・突破の演出
        if (!this.burstDone && this.alt > Proto10.CLOUD_LO - 20) {
          this.burstDone = true
          this.ctx.sfx.play('whoosh')
          this.ctx.sfx.play('wind')
          this.ctx.setCaption('雲を——突き抜ける！')
        }
        if (!this.cloudGone && this.alt > Proto10.CLOUD_HI + 4) {
          // ★スパッと抜ける: 白閃と同時に雲を即座に消して視界を開く
          this.cloudGone = true
          fm.opacity = 1
          this.ctx.sfx.play('drum')
          this.ctx.setCaption('——抜けた！')
        }
        if (this.cloudGone) {
          for (const c of this.clouds) {
            const m = c.material as THREE.MeshBasicMaterial
            m.opacity = Math.max(0, m.opacity - dt * 2.4)
            if (m.opacity <= 0.01) c.visible = false
          }
        }
        if (this.alt >= Proto10.TOP - 1) {
          this.phase = 'orbit'
          this.phaseT = 0
          this.ctx.setCaption('日本列島——青い星の上に、ほんものの国のかたちが浮かんでいる')
        }
        break
      }

      case 'orbit': {
        // 地球ビュー: 超スピードで引き続け、実写の日本列島が「青い星」のように浮かぶ
        const u = Math.min(1, this.phaseT / 4.5)
        const e = 1 - Math.pow(1 - u, 3)
        // 対数補間ズーム: 1 → SCALE_END（約1/1600）。実写地図そのものを縮めて列島全景に
        const s = Math.exp(THREE.MathUtils.lerp(0, Math.log(Proto10.SCALE_END), e))
        this.ctx.aerial.root.scale.setScalar(s)
        this.ctx.aerial.updateForAltitude(Proto10.TOP / s) // 見かけ高度で z8→z7/z6 へ完全移行
        this.pin.visible = false
        // ヒーローショット: 水平飛行の視線から、列島を見下ろす軌道視点へ引き上げる
        if (this.ctx.view.xrActive) {
          this.ctx.view.setPose(
            new THREE.Vector3(0, Proto10.TOP, 40 + 300 * e),
            new THREE.Vector3(0, 0, 0),
          )
        } else {
          this.ctx.view.setPose(
            new THREE.Vector3(0, 654 - 94 * e, 30 + 310 * e),
            new THREE.Vector3(0, 600 - 630 * e, -70 + 70 * e),
          )
        }
        this.starTarget = 1
        this.ctx.scene.fog = null
        bg.lerp(this.skySpace, Math.min(1, dt * 3))
        // 大気光と縁マスクで「地球っぽさ」を出す
        this.halo.position.set(0, -60, -520)
        this.halo.scale.setScalar(1900)
        this.earthGlow.position.set(0, -50, 0)
        this.earthGlow.scale.setScalar(1150)
        this.halo.material.opacity = Math.min(0.8, this.halo.material.opacity + dt * 0.4)
        this.earthGlow.material.opacity = Math.min(0.45, this.earthGlow.material.opacity + dt * 0.25)
        if (u > 0.55 && !this.earthMask.visible) this.earthMask.visible = true
        if (u >= 1) {
          this.phase = 'finale'
          this.phaseT = 0
          this.buildFx()
          this.ctx.sfx.play('bell')
          this.ctx.setCaption('青い星の上に、ほんものの日本のかたち——ここに六十六人の友がいた')
        }
        break
      }

      case 'finale': {
        // ヒーローオービット: 列島のまわりをゆっくり回り込みながら光の収束を見る
        if (!this.ctx.view.xrActive) {
          const ang = this.phaseT * 0.05
          this.ctx.view.setPose(
            new THREE.Vector3(Math.sin(ang) * 340, 560, Math.cos(ang) * 340),
            new THREE.Vector3(0, -30, 0),
          )
        }
        // 実写の日本列島の上で、12寄進地から三鷹へ光の筋が収束する
        const target = new THREE.Vector3(0, -174, 0) // 泰山荘＝実写地図の原点（ローカル）
        let done = 0
        for (const st of this.streaks) {
          if (this.phaseT < st.delay) continue
          st.t = Math.min(1, st.t + dt / 2.4)
          const k = st.t < 0.5 ? 2 * st.t * st.t : 1 - Math.pow(-2 * st.t + 2, 2) / 2
          const p = st.from.clone().lerp(target, k)
          st.head.position.copy(p)
          ;(st.line.geometry as THREE.BufferGeometry).setFromPoints([st.from, p])
          if (st.t >= 1) {
            if (!st.done) {
              st.done = true
              this.ctx.sfx.play('chime')
            }
            done++
            ;(st.head.material as THREE.SpriteMaterial).opacity = Math.max(0.25, 1 - (this.phaseT - st.delay - 2.4) * 0.5)
          }
        }
        if (done === this.streaks.length && this.streaks.length > 0) {
          if (this.allArrivedT === 0) {
            this.allArrivedT = this.phaseT
            this.centerGlow.visible = true
            this.ctx.sfx.play('drum')
            this.ctx.setCaption('六十六人の勧進、八十九の木片——日本中から、一畳へ')
          }
          this.centerGlow.scale.setScalar(46000 * (1 + 0.1 * Math.sin(this.phaseT * 5)))
        }
        if (this.allArrivedT > 0 && this.phaseT > this.allArrivedT + 9) {
          this.enterGround()
        }
        break
      }
    }
  }

  /** フィナーレ演出（光の筋）を実写地図ローカル座標に構築。1u=4m・原点=泰山荘 */
  private buildFx(): void {
    if (!this.fxBuilt) {
      this.fxBuilt = true
      this.ctx.aerial.root.add(this.fx)
      const U_LON = (111320 * Math.cos((35.6832 * Math.PI) / 180)) / 4 // 経度1°→u
      const U_LAT = 110574 / 4 // 緯度1°→u
      const lineMat = new THREE.LineBasicMaterial({
        color: 0xffc860, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false,
      })
      for (const site of SITES) {
        const from = new THREE.Vector3(
          (site.lon - 139.5223) * U_LON,
          -173,
          -(site.lat - 35.6832) * U_LAT,
        )
        const head = Proto10.makeGlow()
        head.scale.setScalar(30000)
        head.position.copy(from)
        this.fx.add(head)
        const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints([from, from]), lineMat.clone())
        this.fx.add(line)
        this.streaks.push({ line, head, from, t: 0, delay: 0.6 + this.streaks.length * 0.35, done: false })
      }
      this.centerGlow = Proto10.makeGlow()
      this.centerGlow.position.set(0, -172, 0)
      this.centerGlow.scale.setScalar(40000)
      this.centerGlow.visible = false
      this.fx.add(this.centerGlow)
    }
    this.fx.visible = true
  }

  private resetFx(): void {
    this.allArrivedT = 0
    this.fx.visible = false
    for (const st of this.streaks) {
      st.t = 0
      st.done = false
      st.head.position.copy(st.from)
      ;(st.head.material as THREE.SpriteMaterial).opacity = 1
      ;(st.line.geometry as THREE.BufferGeometry).setFromPoints([st.from, st.from])
    }
    if (this.centerGlow) this.centerGlow.visible = false
  }

  protected onDispose(): void {
    this.resetFx()
    if (this.fxBuilt) this.ctx.aerial.root.remove(this.fx)
    this.fxBuilt = false
    this.streaks.length = 0
    this.ctx.aerial.root.visible = false
    this.ctx.aerial.setAllOpacity(1)
    this.ctx.aerial.root.scale.setScalar(1)
    this.ctx.aerial.root.position.set(0, 0, 0)
    this.ctx.scene.fog = null
    this.ctx.deck.setVisible(this.ctx.view.xrActive)
    this.ctx.narrator.root.visible = this.ctx.view.xrActive
  }

  /** 縁を丸く宇宙色へ溶かすマスク（中心透明→外周が空の色） */
  private static makeEarthMask(space: THREE.Color): THREE.Mesh {
    const c = document.createElement('canvas')
    c.width = c.height = 512
    const ctx = c.getContext('2d')!
    const col = `#${space.getHexString()}`
    const g = ctx.createRadialGradient(256, 256, 0, 256, 256, 256)
    g.addColorStop(0, `${col}00`)
    g.addColorStop(0.34, `${col}00`)
    g.addColorStop(0.52, `${col}cc`)
    g.addColorStop(0.66, `${col}ff`)
    g.addColorStop(1, `${col}ff`)
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 512, 512)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }),
    )
  }

  private static makePin(color: number): THREE.Sprite {
    const c = document.createElement('canvas')
    c.width = c.height = 128
    const ctx = c.getContext('2d')!
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`
    ctx.beginPath()
    ctx.arc(64, 44, 30, Math.PI * 0.98, Math.PI * 2.02)
    ctx.lineTo(64, 112)
    ctx.closePath()
    ctx.fill()
    ctx.fillStyle = '#fff'
    ctx.beginPath()
    ctx.arc(64, 44, 12, 0, Math.PI * 2)
    ctx.fill()
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }))
  }

  private static makeGlowTexture(): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = c.height = 128
    const ctx = c.getContext('2d')!
    const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64)
    g.addColorStop(0, 'rgba(255,225,150,1)')
    g.addColorStop(0.5, 'rgba(255,190,90,0.45)')
    g.addColorStop(1, 'rgba(255,180,80,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 128, 128)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return tex
  }

  private static makeGlow(): THREE.Sprite {
    return new THREE.Sprite(new THREE.SpriteMaterial({
      map: Proto10.makeGlowTexture(), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false,
    }))
  }

  private static makeAtmosphereHalo(): THREE.Sprite {
    const c = document.createElement('canvas')
    c.width = c.height = 256
    const ctx = c.getContext('2d')!
    const g = ctx.createRadialGradient(128, 128, 60, 128, 128, 128)
    g.addColorStop(0, 'rgba(70,140,255,0)')
    g.addColorStop(0.72, 'rgba(90,160,255,0.55)')
    g.addColorStop(0.88, 'rgba(160,210,255,0.35)')
    g.addColorStop(1, 'rgba(200,230,255,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 256, 256)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return new THREE.Sprite(new THREE.SpriteMaterial({
      map: tex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false,
    }))
  }

  private static makeCloudTexture(): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = c.height = 512
    const ctx = c.getContext('2d')!
    for (let i = 0; i < 340; i++) {
      const x = Math.random() * 512
      const y = Math.random() * 512
      const r = 26 + Math.random() * 74
      const g = ctx.createRadialGradient(x, y, 0, x, y, r)
      const a = 0.07 + Math.random() * 0.13
      g.addColorStop(0, `rgba(242,245,250,${a})`)
      g.addColorStop(1, 'rgba(242,245,250,0)')
      ctx.fillStyle = g
      ctx.fillRect(x - r, y - r, r * 2, r * 2)
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    return tex
  }
}
